import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { clearLiveMessageCache } from '../../lib/liveMessages';
import { queryKeys } from '../../lib/queryClient';
import { getSocket } from '../../lib/socket';
import type { Capabilities } from '../../types';
import { getEvents } from './eventsApi';
import { getAdminUsers, getMessages } from './shellApi';

export function useShellData(caps: Capabilities) {
  const queryClient = useQueryClient();
  const events = useQuery({
    queryKey: queryKeys.events,
    queryFn: ({ signal }) => getEvents(signal),
    refetchInterval: 30_000,
  });
  const messages = useQuery({
    queryKey: queryKeys.messages,
    queryFn: getMessages,
    enabled: caps.moderate,
    refetchInterval: caps.moderate ? 30_000 : false,
  });
  const adminUsers = useQuery({
    queryKey: queryKeys.adminUsers,
    queryFn: getAdminUsers,
    enabled: caps.admin,
    refetchInterval: caps.admin ? 30_000 : false,
  });

  useEffect(() => {
    const socket = getSocket();
    let refreshTimer: number | null = null;
    const refresh = () => {
      if (refreshTimer !== null) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void queryClient.invalidateQueries({ queryKey: queryKeys.events });
        if (caps.moderate) void queryClient.invalidateQueries({ queryKey: queryKeys.messages });
        if (caps.admin) void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      }, 8_000);
    };
    const refreshNow = () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.events });
      if (caps.moderate) void queryClient.invalidateQueries({ queryKey: queryKeys.messages });
      if (caps.admin) void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
    };
    const resetMessages = () => { void clearLiveMessageCache(queryClient); };

    socket.on('connect', refreshNow);
    socket.on('events_update', refresh);
    socket.on('new_message', refresh);
    socket.on('message_updated', refresh);
    socket.on('pin_update', refresh);
    socket.on('messages_reset', resetMessages);
    return () => {
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      socket.off('connect', refreshNow);
      socket.off('events_update', refresh);
      socket.off('new_message', refresh);
      socket.off('message_updated', refresh);
      socket.off('pin_update', refresh);
      socket.off('messages_reset', resetMessages);
    };
  }, [caps.admin, caps.moderate, queryClient]);

  return {
    events: events.data ?? [],
    teamRooms: (events.data ?? []).filter((event) => event.team_shared),
    live: (events.data ?? []).some((event) => event.active || event.status === 'started'),
    badges: {
      events: events.data?.length ?? 0,
      mod: messages.data?.filter((message) => !message.deleted && !message.answered).length ?? 0,
      admin: adminUsers.data?.filter((user) => !user.approved).length ?? 0,
    },
  };
}
